import { totalWeightForLineItem } from "@/Helpers/lineItemCalculations";
import type { OpenClaimLineItem } from "@freightsimple/generated-dashboard-openapi-client/models/OpenClaimLineItem";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import { FreightClaimType } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimType";
import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { ClaimsCurrencyInput } from "../Inputs/ClaimsCurrencyInput";
import { ClaimsTypeDropdown } from "../Dropdowns/ClaimsTypeDropdown";
import { ClaimsItemConditionDropdown } from "../Dropdowns/ClaimsItemConditionDropdown";
import { ClaimsItemRepairabilityDropdown } from "../Dropdowns/ClaimsItemRepairabilityDropdown";
import { ClaimsPiecesInput } from "../Inputs/ClaimsPiecesInput";

interface DamageClaimLineItemRowProps {
  lineItem: LineItem;
  index: number;
  currency: Currency;
  claimLineItem: OpenClaimLineItem;
  setClaimLineItem: (_: OpenClaimLineItem) => void;
  forceValidation: boolean;
  disabled?: boolean | undefined;
}

/**
 * One line item of the shipment, with the claim being made against it.
 *
 * The claim detail is a divided second half of the same card rather than a
 * second bordered box inside the first — a box inside a box inside a numbered
 * section is three frames deep for one question.
 */
export function ClaimsDamageClaimLineItemRow(
  props: DamageClaimLineItemRowProps,
) {
  const { lineItem, index, claimLineItem, disabled } = props;
  const {
    claimType,
    amountPerPiece,
    affectedPieces,
    totalPieces,
    condition,
    repairability,
  } = claimLineItem;

  const isClaimed = claimType !== FreightClaimType.NoDamageOrLoss;
  const isDamage = claimType === FreightClaimType.Damage;

  function update(changes: Partial<OpenClaimLineItem>) {
    if (disabled) return;
    props.setClaimLineItem({ ...claimLineItem, ...changes });
  }

  /**
   * Clearing a number field drops the key rather than setting it to
   * `undefined`. Under `exactOptionalPropertyTypes` an optional `number?` can't
   * hold an explicit `undefined`, and omitting it also keeps the submitted
   * claim free of empty fields.
   */
  function updateNumber(
    field: "affectedPieces" | "totalPieces" | "amountPerPiece",
    value: number | undefined,
  ) {
    if (disabled) return;
    const next = { ...claimLineItem };
    if (value === undefined) {
      delete next[field];
    } else {
      next[field] = value;
    }
    props.setClaimLineItem(next);
  }

  function errorMessageForAffectedPieces() {
    if (affectedPieces === undefined) {
      return "Required";
    }

    if (affectedPieces === 0) {
      return "Cannot be zero";
    }

    if (totalPieces !== undefined && affectedPieces > totalPieces) {
      return "Too high";
    }

    return undefined;
  }

  function errorMessageForTotalPieces() {
    if (totalPieces === undefined) {
      return "Required";
    }

    if (totalPieces === 0) {
      return "Cannot be zero";
    }

    return undefined;
  }

  function errorMessageForAmountPerPiece() {
    if (amountPerPiece === undefined) {
      return "Required";
    }

    if (amountPerPiece < 0.01) {
      return "Cannot be zero";
    }

    return undefined;
  }

  // Only worth showing once both halves of the multiplication are real numbers.
  const lineTotal =
    affectedPieces !== undefined &&
    affectedPieces > 0 &&
    amountPerPiece !== undefined &&
    amountPerPiece > 0
      ? affectedPieces * amountPerPiece
      : undefined;

  return (
    <Panel>
      <Stack gap={4}>
        <Row gap={4} align="center" justify="between" wrap>
          <Row gap={4} align="center" shrinkable>
            <T size="lg" weight="semibold">
              {lineItem.numberHandlingUnits} x {lineItem.handlingUnitType}
            </T>
            <Stack gap={0} align="start">
              <T size="base" weight="medium">
                {lineItem.description}
              </T>
              <T size="sm" color="secondary">
                {lineItem.length}"x{lineItem.width}"x{lineItem.height}",{" "}
                {totalWeightForLineItem(lineItem)}lb total
              </T>
            </Stack>
          </Row>
          <ClaimsTypeDropdown
            claimType={claimType}
            setClaimType={(newClaimType) => update({ claimType: newClaimType })}
          />
        </Row>

        {isClaimed && (
          <>
            <Divider />
            <Stack gap={4}>
              <T size="base" color="secondary">
                {isDamage
                  ? "Please tell us the complete details of the damage to pieces in this line item"
                  : "Please tell us the complete details of the loss of pieces in this line item"}
              </T>

              <Row gap={6} align="start" wrap>
                <ClaimsPiecesInput
                  dataTestId="affected-pieces-input"
                  label={isDamage ? "Damaged Pieces" : "Lost Pieces"}
                  value={affectedPieces}
                  onChange={(newValue) =>
                    updateNumber("affectedPieces", newValue)
                  }
                  errorMessage={errorMessageForAffectedPieces()}
                  forceValidation={props.forceValidation}
                />
                <ClaimsPiecesInput
                  dataTestId="total-pieces-input"
                  label="Total Pieces"
                  value={totalPieces}
                  onChange={(newValue) => updateNumber("totalPieces", newValue)}
                  errorMessage={errorMessageForTotalPieces()}
                  forceValidation={props.forceValidation}
                />
                <ClaimsCurrencyInput
                  dataTestId="piece-value-input"
                  label="Piece Value"
                  currency={props.currency}
                  value={amountPerPiece}
                  onChange={(newValue) =>
                    updateNumber("amountPerPiece", newValue)
                  }
                  errorMessage={errorMessageForAmountPerPiece()}
                  forceValidation={props.forceValidation}
                  placeholder="Enter Amount"
                />
                <FormItem label="Condition" disabled={disabled}>
                  <ClaimsItemConditionDropdown
                    itemCondition={condition}
                    setItemCondition={(newCondition) =>
                      update({ condition: newCondition })
                    }
                  />
                </FormItem>
                {/* Repairability is a damage question — nothing about a lost
                    piece can be repaired, and asking anyway made customers pick
                    a value that the claim then ignores. */}
                {isDamage && (
                  <FormItem label="Repairability" disabled={disabled}>
                    <ClaimsItemRepairabilityDropdown
                      id={`item-repairability-dropdown-${index}`}
                      repairability={repairability}
                      setRepairability={(newRepairability) =>
                        update({ repairability: newRepairability })
                      }
                    />
                  </FormItem>
                )}
              </Row>

              {lineTotal !== undefined && (
                <T size="base" color="secondary">
                  Total claim for {affectedPieces} pieces: $
                  {lineTotal.toLocaleString()} {props.currency}
                </T>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Panel>
  );
}
